import { Box } from "@mui/material";
import { Plugin } from "chart.js";
import { useMemo, useRef } from "react";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../../context/CurrencyContext";
import { useTheme } from "../../../context/ThemeContext";
import {
  getLegendLabels,
  getTicks,
  getTooltip,
} from "../../../utils/chartjsTheming";
import { OfferMark, SettlementChartData } from "./SettlementChartData";

/** The height of the chart with its legend, in px. */
const CHART_HEIGHT = 340;

/**
 * The bargaining range of a party against an opponent by stage, with the
 * offers between the two as numbered points. A click selects the stage it is
 * nearest to.
 */
export const SettlementChart = ({
  chartData,
  marks = [],
  index = 0,
  onIndexSelect,
}: {
  chartData: SettlementChartData;
  /** The datasets that are the points of offers, with their numbers. */
  marks?: OfferMark[];
  index?: number;
  onIndexSelect: (newIndex: number) => void;
}) => {
  const { currency } = useCurrency();
  const { theme } = useTheme();
  const { t } = useTranslation();

  // A chart takes its plugins when it is made, so the one that writes the
  // numbers reads what it writes from here at every drawing.
  const drawn = useRef({ marks, family: theme.typography.fontFamily });
  drawn.current = { marks, family: theme.typography.fontFamily };
  const offerNumbers: Plugin<"line"> = useMemo(
    () => ({
      id: "offerNumbers",
      afterDatasetsDraw: (chart) => {
        const { ctx } = chart;
        ctx.save();
        ctx.font = `700 12px ${drawn.current.family}`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        for (const mark of drawn.current.marks) {
          const { datasetIndex, stageIndex, number, ink } = mark;
          const point = chart.getDatasetMeta(datasetIndex).data[stageIndex];
          if (!point) continue;
          ctx.fillStyle = ink;
          ctx.fillText(String(number), point.x, point.y + 0.5);
        }
        ctx.restore();
      },
    }),
    [],
  );

  const stages = chartData.labels?.length ?? 0;
  const offerDatasets = new Set(marks.map(({ datasetIndex }) => datasetIndex));

  return (
    <Box sx={{ height: CHART_HEIGHT, userSelect: "none" }}>
      <Line
        data={chartData}
        plugins={[offerNumbers]}
        options={{
          // the chart fills the frame it is given rather than keeping a shape
          maintainAspectRatio: false,
          onClick: (event, _, chart) => {
            if (event.x === null) return;
            const value = chart.scales.x.getValueForPixel(event.x);
            if (value === undefined) return;
            const stageIndex = Math.round(value);
            if (stageIndex >= 0 && stageIndex < stages)
              onIndexSelect(stageIndex);
          },
          interaction: {
            mode: "point",
          },
          plugins: {
            legend: {
              onClick: () => {
                // do nothing
              },
              position: "bottom",
              align: "start",
              labels: {
                ...getLegendLabels(theme),
                boxWidth: 16,
                boxHeight: 2,
                padding: 16,
                // the points of the offers are told by their numbers, and a
                // line that starts anew after a settlement is named once
                filter: ({ datasetIndex, text }, { datasets }) =>
                  datasetIndex !== undefined &&
                  !offerDatasets.has(datasetIndex) &&
                  datasets.findIndex(({ label }) => label === text) ===
                    datasetIndex,
              },
            },
            tooltip: {
              ...getTooltip(theme),
              callbacks: {
                title: () => "",
                label: (item) =>
                  t("charts.tooltip.labelledValue", {
                    label: item.dataset.label,
                    value: currency.format(item.parsed.y ?? 0),
                  }),
              },
              itemSort: (a, b) => (b.parsed.y ?? 0) - (a.parsed.y ?? 0),
            },
          },
          scales: {
            x: {
              ticks: {
                ...getTicks(theme),
                maxRotation: 0,
                autoSkip: false,
                // the stage whose values stand over the chart
                font: (context) => ({
                  ...getTicks(theme).font,
                  weight: context.index === index ? "bold" : "normal",
                }),
              },
              grid: {
                lineWidth: 1.5,
                color: (context) =>
                  context.index === index
                    ? theme.palette.primary.main
                    : "transparent",
              },
              border: { dash: [6, 4] },
            },
            y: {
              beginAtZero: true,
              grid: {
                // the line of zero stands out from the lines of the other amounts
                color: (context) =>
                  context.tick.value === 0
                    ? theme.palette.text.disabled
                    : theme.palette.divider,
              },
              ticks: {
                ...getTicks(theme),
                callback: function (value) {
                  if (typeof value === "number")
                    return currency.formatCompact(value);
                  return value;
                },
              },
            },
          },
        }}
      />
    </Box>
  );
};
